import React, { ChangeEvent, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Checkbox, Heading, Icons, TextInput } from '@sede-enterprise/shell-ds-react-framework';

import dayjs from 'dayjs';
import { useSelector } from 'react-redux';

import Table from '../../FieldNomination/Table';
import { ITableRef } from '../../FieldNomination/Table/Table.type';
import Button from '../../../../UI/Button';
import { Col, FormRow, Row } from '../../../../UI/Grid';
import {
  DOWNLOAD_TERMINAL_MAPPINGS,
  GET_SWING_CONTRACT_MAPPING_AUDIT_LOG_INFO,
} from '../../../../constants/FieldNomsSwingContract';
import useGetSwingContractMappings from '../../../../hooks/fieldNomsSwingContract/Mapping/useGetSwingContractMappings';
import { IFullState } from '../../../../types/All.type';
import { IFieldType, SwingContractMapping } from '../../../../types/FieldNomsSwingContract.type';
import { handleDownload } from '../../../../utils/common/utils';
import searchByText from '../../../../utils/searchFunction';
import FieldAuditLogs from '../FieldAuditLogs';
import AddEditZKeyMapping from './AddEditZKeyMapping';
import ConfirmDeleteMapping from './DeleteMapping/ConfirmDeleteMapping';
import { DateCell, StatusIconCell } from './columnUtils';
import { AuditTitle, CustomCard } from './style';
import { rolePermissions } from '../../../../constants/Permission';

const { DownloadSolid, ArrowLeft, Edit, TrashAlt: DeleteIcon, InfoCircle } = Icons;

export const checkExisting = (
  keys: string[],
  existingData: SwingContractMapping[],
  formData: SwingContractMapping,
): boolean => {
  return existingData.some((record) => keys.every((key) => record[key] === formData[key]));
};

const ZKeyMapping: React.FC = () => {
  const navigate = useNavigate();

  const { permissions } = useSelector((state: IFullState) => state.genesisUserRolePermission);

  const [open, setOpen] = useState<boolean>(false);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);

  const [downloading, setDownloading] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');

  const [openAudit, setOpenAudit] = useState(false);

  const [isValidContracts, setIsValidContracts] = useState<boolean>(true);
  const [selectedItem, setSelectedItem] = useState<SwingContractMapping | null>(null);

  const { swingContractMappings, loading, getSwingContractMappings } =
    useGetSwingContractMappings(isValidContracts);

  const tableRef = useRef<ITableRef>(null);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value);

    if (tableRef?.current?.resetPagination) {
      tableRef.current.resetPagination();
    }
  };

  const handleOnOpenClose = (value: boolean) => {
    setOpen(value);
    if (!value) {
      setSelectedItem(null);
    }
  };

  const handleOnOpenCloseConfirm = (value: boolean) => {
    setOpenConfirm(value);
    if (!value) {
      setSelectedItem(null);
    }
  };

  const handleOnOpenCloseAudit = (value: boolean) => {
    setOpenAudit(value);
    if (!value) {
      setSelectedItem(null);
    }
  };

  const handleDownloadOnClick = async () => {
    setDownloading(true);
    await handleDownload(
      `${DOWNLOAD_TERMINAL_MAPPINGS}?isValid=${isValidContracts}`,
      `Mappings_${dayjs().format()}.xlsx`,
      `POST`,
    );
    setDownloading(false);
  };

  const handleEditOnClick = (item: SwingContractMapping) => {
    setSelectedItem(item);
    handleOnOpenClose(true);
  };

  const handleDeleteOnClick = (item: SwingContractMapping) => {
    setSelectedItem(item);
    handleOnOpenCloseConfirm(true);
  };

  const handleInfoOnClick = (item: SwingContractMapping) => {
    setSelectedItem(item);
    handleOnOpenCloseAudit(true);
  };

  const renderData = (text: string, mappings: SwingContractMapping[]) => {
    return searchByText(mappings, text, [
      'contractName',
      'entryOrExit',
      'description',
      'deal_DealLegId',
      'zKey',
      'locationOrExitMeter',
      'geminiActivityNumber',
      'validFrom',
      'validTo',
      'source',
      'destination',
    ]);
  };

  const renderStatusIconCell = ({ row }) => (
    <StatusIconCell validFrom={row?.original?.validFrom} validTo={row?.original?.validTo} />
  );

  const renderValidFromCell = ({ row }) => <DateCell date={row?.original?.validFrom} />;

  const renderValidToCell = ({ row }) => <DateCell date={row?.original?.validTo} />;

  const renderActionCell = (item: SwingContractMapping) => (
    <div className='grid-actions'>
      <Button
        iconOnly
        icon={<Edit />}
        variant='transparent'
        size='xsmall'
        title='Edit'
        onClick={() => handleEditOnClick(item)}
        disabled={!permissions.includes(rolePermissions.updateContractMapping)}
      />
      <Button
        iconOnly
        icon={<DeleteIcon />}
        variant='transparent'
        size='xsmall'
        title='Delete'
        onClick={() => handleDeleteOnClick(item)}
        disabled={!permissions.includes(rolePermissions.deleteContractMapping)}
      />
      <Button
        iconOnly
        icon={<InfoCircle />}
        variant='transparent'
        size='xsmall'
        title='Audit Logs'
        onClick={() => handleInfoOnClick(item)}
      />
    </div>
  );

  const columns = [
    {
      header: '',
      accessorKey: '',
      width: 20,
      cell: renderStatusIconCell,
    },
    {
      accessorKey: 'contractName',
      header: 'Contract Name',
    },
    {
      accessorKey: 'entryOrExit',
      header: 'Entry/Exit',
    },
    {
      accessorKey: 'description',
      header: 'Description',
    },
    {
      accessorKey: 'deal_DealLegId',
      header: 'Deal ID',
      cell: ({ getValue }) => getValue() ?? '-',
    },
    {
      accessorKey: 'dealUnit',
      header: 'Deal Unit',
      cell: ({ getValue }) => getValue() ?? '-',
    },
    {
      accessorKey: 'zKey',
      header: 'ZKey',
      cell: ({ getValue }) => getValue() ?? '-',
    },
    {
      accessorKey: 'locationOrExitMeter',
      header: 'Location/Exit Meter',
    },
    {
      accessorKey: 'geminiActivityNumber',
      header: 'Gemini Activity Number',
    },
    {
      accessorKey: '',
      header: 'Valid From',
      cell: renderValidFromCell,
    },
    {
      accessorKey: '',
      header: 'Valid To',
      cell: renderValidToCell,
    },
    {
      accessorKey: 'source',
      header: 'Source',
    },
    {
      accessorKey: 'destination',
      header: 'Destination',
      cell: ({ getValue }) => getValue() ?? '-',
    },
    {
      accessorKey: '',
      header: 'Actions',
      width: 80,
      cell: ({ row }) => renderActionCell(row?.original),
    },
  ];

  const filteredData = renderData(searchText, swingContractMappings);

  return (
    <>
      <CustomCard>
        <Row>
          <Col size={12}>
            <div className='heading-container'>
              <Button
                className='close-mapping'
                iconOnly
                icon={<ArrowLeft />}
                onClick={() => navigate(-1)}
                variant='transparent'
              />
              <Heading type='h2'>Deal ID Mapping</Heading>
            </div>
            <FormRow>
              <Col size={6}>
                <div className='search-filter-container'>
                  <div className='search-input'>
                    <TextInput
                      size='small'
                      placeholder='Search mappings...'
                      onChange={handleSearch}
                    />
                  </div>
                  <div className='checkbox-container'>
                    <Checkbox
                      size='large'
                      label='Valid Mappings Only'
                      name='Show Valid Mapping'
                      checked={isValidContracts}
                      onChange={() => setIsValidContracts(!isValidContracts)}
                    />
                  </div>
                </div>
              </Col>
              <Col size={6}>
                <div className='action-buttons-container'>
                  <Button
                    size='small'
                    onClick={() => handleOnOpenClose(true)}
                    disabled={!permissions.includes(rolePermissions.createContractMapping)}
                  >
                    Add New Mapping
                  </Button>
                  <Button
                    size='small'
                    icon={<DownloadSolid />}
                    onClick={handleDownloadOnClick}
                    loading={downloading}
                    disabled={
                      !permissions.includes(rolePermissions.downloadContractMapping) || downloading
                    }
                  >
                    Download Mappings
                  </Button>
                </div>
              </Col>
            </FormRow>
            <div className='table-container'>
              <Table
                ref={tableRef}
                loading={loading}
                size='small'
                columns={columns}
                data={filteredData}
                totalRecords={filteredData.length}
              />
            </div>
          </Col>
        </Row>
      </CustomCard>
      {open && (
        <AddEditZKeyMapping
          open={open}
          selectedItem={selectedItem}
          onClose={() => handleOnOpenClose(false)}
          refreshData={() => getSwingContractMappings(isValidContracts)}
        />
      )}
      <ConfirmDeleteMapping
        open={openConfirm}
        selectedItem={selectedItem}
        onClose={() => handleOnOpenCloseConfirm(false)}
        refreshData={() => getSwingContractMappings(isValidContracts)}
      />

      {openAudit && (
        <FieldAuditLogs
          open={openAudit}
          url={`${GET_SWING_CONTRACT_MAPPING_AUDIT_LOG_INFO}?mappingId=${selectedItem?.id}`}
          onClose={() => handleOnOpenCloseAudit(false)}
          modalTitle='Swing Contract Info and Audit Logs'
          fieldType={IFieldType.FIELD_MAPPING}
        >
          <AuditTitle>Audit Logs:</AuditTitle>
        </FieldAuditLogs>
      )}
    </>
  );
};

export default ZKeyMapping;
